// Repo for the AWS AI Cost Trends module. Mirrors the existing
// AIGovernanceRepo / SavingsManagementRepo / MapManagementRepo pattern:
// constructor takes RepoParams from the shared repoFactory, all live
// operations dispatch through the codebase's GraphQLClient. Hooks
// resolve this repo via useApp().repoFactory.aiCostTrendsRepo().

import { GET_AI_SPEND_TRENDS } from 'graphql/ai-cost-trends/getAISpendTrends'
import { GET_AI_SPEND_TRENDS_REPORT } from 'graphql/ai-cost-trends/getAISpendTrendsReport'
import GraphQLClient from 'ts/GraphQLClient'
import { RepoParams } from 'ts/types'

export interface AISpendTrendEntry {
  budgetId: string
  budgetName: string | null
  serviceCode: string
  serviceName: string
  // The wire shape for the 3-day claimed/shared breakdown isn't pinned
  // at the resolver level yet — the field could land as AWSJSON, an
  // array of per-day objects, or a scalar integer. Typed as `unknown`
  // here so the adapter handles the variants defensively without
  // forcing the rest of the module to refactor when the contract pins
  // down.
  days: unknown
  totalClaimed: number
  totalShared: number
  totalCost: number
  deltaPercent: number | null
  isNew: boolean
  // AWSJSON string carrying { inputTokens, outputTokens, cacheReadTokens }
  // for Bedrock workloads; null for services that don't surface tokens
  // (AgentCore, SageMaker, Amazon Q).
  tokens: string | null
}

export interface AISpendTrendsReportRow {
  usageDate: string
  element4Map: string | null
  budgetName: string | null
  serviceCode: string
  serviceName: string
  accountId: string
  accountName: string
  claimed: number
  shared: number
  totalCost: number
  allocationAnchorType: string
  tokens: string | null
}

export interface AISpendTrendsServiceBreakdown {
  serviceCode: string
  serviceName: string
  totalCost: number
  totalClaimed: number
  totalShared: number
}

export interface AISpendTrendsReport {
  rows: AISpendTrendsReportRow[]
  summary: {
    totalClaimed: number
    totalShared: number
    totalCost: number
    serviceBreakdown: AISpendTrendsServiceBreakdown[]
  } | null
  startDate: string
  endDate: string
}

export default class AICostTrendsRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, ID_TOKEN }: RepoParams) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, idToken: ID_TOKEN })
  }

  async getAISpendTrends(days: number = 3): Promise<AISpendTrendEntry[]> {
    const res = await this.client.request<{ getAISpendTrends: AISpendTrendEntry[] | null }>({
      query: GET_AI_SPEND_TRENDS,
      variables: { days }
    })
    return res.getAISpendTrends ?? []
  }

  async getAISpendTrendsReport(input: {
    startDate: string
    endDate: string
    serviceCode?: string
  }): Promise<AISpendTrendsReport | null> {
    const res = await this.client.request<{ getAISpendTrendsReport: AISpendTrendsReport | null }>({
      query: GET_AI_SPEND_TRENDS_REPORT,
      variables: input
    })
    return res.getAISpendTrendsReport ?? null
  }
}
